import React, { useState, useEffect } from 'react';
import { CheckIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

import { StackableModal, FilterDropdown } from '../components/nextgen';
import Button from '../components/Button';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';
import { getContacts } from '../api/contacts';

const MODAL_NAME = 'vendorPicker';

const VendorPickerModal = () => {
	const { open: options, setClose } = useModal(MODAL_NAME);
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [search, setSearch] = useState('');
	const [typeFilter, setTypeFilter] = useState('all');

	const { value: contacts, execute: fetchContacts, status } = useAsync(
		getContacts,
		{ immediate: false }
	);

	useEffect(() => {
		if (options) {
			fetchContacts({ pageSize: null });
			setSelectedContacts([]);
			setSearch('');
			setTypeFilter('all');
		}
	}, [options, fetchContacts]);

	const handleSelect = (contact) => {
		if (options?.multiSelect) {
			if (selectedContacts.find((c) => c.id === contact.id)) {
				setSelectedContacts(selectedContacts.filter((c) => c.id !== contact.id));
			} else {
				const maxSelect = options?.maxSelect || Infinity;
				if (selectedContacts.length < maxSelect) {
					setSelectedContacts([...selectedContacts, contact]);
				}
			}
		} else {
			options?.onSelect?.([contact]);
			setClose();
		}
	};

	const handleConfirm = () => {
		if (options?.onSelect && selectedContacts.length > 0) {
			options.onSelect(selectedContacts);
		}
		setClose();
	};

	const getContactDisplay = (contact) => {
		if (contact.type === 'company') {
			return {
				name: contact.companyName || 'Unnamed Company',
				subtitle: contact.location ? `Location: ${contact.location}` : null,
			};
		} else {
			const name = [contact.firstName, contact.lastName]
				.filter(Boolean)
				.join(' ') || 'Unnamed Contact';
			const parts = [];
			if (contact.title) parts.push(contact.title);
			if (contact.company?.companyName) parts.push(`at ${contact.company.companyName}`);
			return {
				name,
				subtitle: parts.length > 0 ? parts.join(' ') : null,
			};
		}
	};

	const filteredContacts = contacts?.rows?.filter((contact) => {
		// Search filter
		if (search) {
			const searchLower = search.toLowerCase();
			const matchesSearch =
				contact?.firstName?.toLowerCase().includes(searchLower) ||
				contact?.lastName?.toLowerCase().includes(searchLower) ||
				contact?.companyName?.toLowerCase().includes(searchLower) ||
				contact?.title?.toLowerCase().includes(searchLower) ||
				contact?.company?.companyName?.toLowerCase().includes(searchLower);
			if (!matchesSearch) return false;
		}

		// Type filter
		if (typeFilter !== 'all') {
			if (typeFilter === 'person' && contact.type !== 'person') {
				return false;
			}
			if (typeFilter === 'company' && contact.type !== 'company') {
				return false;
			}
		}

		return true;
	}) || [];

	// Filter out contacts that are already attached to the asset
	const skip = options?.skip || [];
	const availableContacts = filteredContacts
		.filter((contact) => !skip.includes(contact.id))
		.sort((a, b) => {
			// Get display names for sorting
			const nameA = (a.type === 'company'
				? a.companyName
				: [a.firstName, a.lastName].filter(Boolean).join(' ')
			) || '';
			const nameB = (b.type === 'company'
				? b.companyName
				: [b.firstName, b.lastName].filter(Boolean).join(' ')
			) || '';

			return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
		});

	const isLoading = status === 'pending';
	const isSelected = (contactId) => selectedContacts.some((c) => c.id === contactId);

	return (
		<StackableModal
			open={!!options}
			onClose={setClose}
			title={options?.title || 'Select Vendor'}
			size="2xl"
		>
			<div className="space-y-4">
				{/* Search and Filters */}
				<div className="flex gap-3">
					<input
						type="text"
						placeholder="Search contacts..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
					/>
					<FilterDropdown
						label="Type"
						placeholder="All Types"
						value={typeFilter}
						options={[
							{ value: 'all', label: 'All Types' },
							{ value: 'person', label: 'Person' },
							{ value: 'company', label: 'Company' },
						]}
						onChange={setTypeFilter}
					/>
					{options?.showCreateButton && (
						<Button
							type="secondary"
							onClick={() => {
								setClose();
								if (options?.onCreateNew) {
									options.onCreateNew();
								}
							}}
						>
							Create New Vendor
						</Button>
					)}
				</div>

				{/* Contact List */}
				<div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
					{isLoading ? (
						<div className="p-8 text-center text-gray-500">
							Loading contacts...
						</div>
					) : availableContacts.length === 0 ? (
						<div className="p-8 text-center text-gray-500">
							{search || typeFilter !== 'all'
								? 'No contacts match your filters'
								: 'No contacts available'}
						</div>
					) : (
						<ul className="divide-y divide-gray-200">
							{availableContacts.map((contact) => {
								const display = getContactDisplay(contact);
								const selected = isSelected(contact.id);

								return (
									<li
										key={contact.id}
										onClick={() => handleSelect(contact)}
										className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
											selected ? 'bg-indigo-50' : ''
										}`}
									>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3 flex-1 min-w-0">
												<div className="flex-shrink-0">
													{contact.type === 'person' ? (
														<UserIcon className="w-5 h-5 text-gray-400" />
													) : (
														<BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
													)}
												</div>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-gray-900 truncate">
														{display.name}
													</p>
													{display.subtitle && (
														<p className="text-xs text-gray-500 truncate">
															{display.subtitle}
														</p>
													)}
												</div>
											</div>
											{selected && (
												<CheckIcon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
											)}
										</div>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				{/* Footer with action buttons */}
				<div className="flex justify-between items-center pt-4">
					<div className="text-sm text-gray-500">
						{selectedContacts.length > 0 && (
							<span>{selectedContacts.length} selected</span>
						)}
					</div>
					<div className="flex gap-3">
						<Button type="secondary" onClick={() => setClose()}>
							Cancel
						</Button>
						{options?.multiSelect && (
							<Button
								onClick={handleConfirm}
								disabled={selectedContacts.length === 0}
							>
								Add {selectedContacts.length > 0 ? `(${selectedContacts.length})` : ''}
							</Button>
						)}
					</div>
				</div>
			</div>
		</StackableModal>
	);
};

export default VendorPickerModal;
